'use client';

import { Component, type ErrorInfo, type ReactNode } from 'react';
import { client } from '../../utils/trpc';
interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}
interface ErrorBoundaryProps {
  children: ReactNode;
}
class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      error: null
    };
  }
  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return {
      hasError: true,
      error
    };
  }
  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Report to server (fire and forget)
    client.users.error.mutate({
      error: `Next.js Component Error: ${error.message}`,
      stack: `${error.stack || 'No stack trace'}\n\nComponent Stack:${errorInfo.componentStack || 'N/A'}`,
      source: 'client'
    }).catch(() => {
      // Silently fail - can't do much if error reporting fails
    });
  }
  handleRetry = () => {
    this.setState({
      hasError: false,
      error: null
    });
  };
  render() {
    if (this.state.hasError) {
      return <div className="flex min-h-screen items-center justify-center bg-background-primary">
          <div className="w-full max-w-md space-y-8 p-5">
            <div className="text-center">
              <h2 className="mt-6 text-3xl font-extrabold text-textPrimary">
                Something went wrong
              </h2>
              <p className="mt-2 text-sm text-textSecondary">
                The app encountered an unexpected error. We&apos;ve been
                notified and are working on a fix.
              </p>
              <div className="mt-5">
                <button onClick={this.handleRetry} className="group relative flex w-full justify-center rounded-full border border-transparent bg-primary px-6 py-2.5 text-sm font-emphasis text-white hover:bg-indigo-400 focus:outline-none focus:ring-2 focus:ring-primary/20">
                  Try Again
                </button>
              </div>
            </div>
          </div>
        </div>;
    }
    return this.props.children;
  }
}
export default ErrorBoundary;